import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { Check, History, Pencil, Sprout, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Concept } from "@/types/concept";
import { useUpdateConcept } from "@/hooks/useConcepts";
import { isPaidPlan, useCurrentUser } from "@/hooks/useCurrentUser";
import { guideSignals } from "@/lib/guideSignals";
import { ConceptInfo } from "./ConceptInfo";
import { ConceptHistory } from "./ConceptHistory";
import { ClusterOverview } from "./ClusterOverview";
import { RefineConceptModal } from "./RefineConceptModal";
import { useRescoreToast } from "./RescoreToast";

interface Props {
  concept: Concept;
  paperCount?: number;
  seedCount: number;
}

export function ConceptPopover({ concept, paperCount, seedCount }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(concept.name);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const updateConcept = useUpdateConcept(concept.id);
  const [refineOpen, setRefineOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const { data: currentUser } = useCurrentUser();
  const { promptRescore } = useRescoreToast(concept.id);

  const handleRefineClick = useCallback(() => {
    if (!isPaidPlan(currentUser?.plan)) {
      toast.error("AI refinement requires a paid plan");
      return;
    }
    setRefineOpen(true);
  }, [currentUser?.plan]);

  // Note: the legacy `guide:open-refine-modal` / `guide:open-concept-refinement`
  // window listeners (carried in the old ConceptSidebar) were dropped here — they
  // had no remaining dispatchers after the JourneyBar was retired, and as popover
  // content this component only mounts while open, so they couldn't fire reliably.
  // Refinement is reached via the "Refine with AI" button (handleRefineClick).

  useEffect(() => {
    setDraft(concept.name);
    setEditing(false);
  }, [concept.id, concept.name]);

  useEffect(() => {
    if (editing) textareaRef.current?.select();
  }, [editing]);

  const handleRefineAccepted = useCallback(
    (_basedOnVersionId: string | null) => {
      guideSignals.mark(concept.id, "hasRefinedConcept");
      promptRescore();
    },
    [concept.id, promptRescore],
  );

  const commitEdit = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== concept.name) updateConcept.mutate({ name: trimmed });
    setEditing(false);
  }, [draft, concept.name, updateConcept]);

  const cancelEdit = useCallback(() => {
    setDraft(concept.name);
    setEditing(false);
  }, [concept.name]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") commitEdit();
      if (e.key === "Escape") cancelEdit();
    },
    [commitEdit, cancelEdit],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Title + edit */}
      <div className="flex items-start gap-2 border-b pb-3">
        {editing ? (
          <div className="flex-1">
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={commitEdit}
              rows={2}
              className="w-full resize-none rounded-md border border-input bg-background px-2 py-1 text-base font-semibold leading-snug focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <div className="mt-1 flex gap-1">
              <Button variant="ghost" size="icon-sm" onClick={commitEdit} title="Save">
                <Check className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onMouseDown={(e) => {
                  e.preventDefault(); // beat the textarea's onBlur commit
                  cancelEdit();
                }}
                title="Cancel"
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
        ) : (
          <h2
            className="group flex-1 cursor-pointer text-base font-semibold leading-snug"
            onClick={() => setEditing(true)}
            title="Click to edit"
          >
            {concept.name}
            <Pencil className="ml-1.5 inline size-3 align-middle text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
          </h2>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          title="Version history"
          aria-label="Version history"
          aria-expanded={historyOpen}
          onClick={() => setHistoryOpen((v) => !v)}
          className={`shrink-0 ${historyOpen ? "bg-accent text-accent-foreground" : ""}`}
        >
          <History className="size-4" />
        </Button>
      </div>

      {/* Version history expands inline (a nested popover would overlap this one). */}
      {historyOpen && (
        <div className="max-h-64 overflow-y-auto border-b pb-3">
          <ConceptHistory conceptId={concept.id} currentName={concept.name} />
        </div>
      )}

      <div data-guide-target="conceptDescription">
        <ConceptInfo concept={concept} hideTitle paperCount={paperCount} onRefineClick={handleRefineClick} />
      </div>

      <ClusterOverview conceptId={concept.id} />

      <div className="flex items-center text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <Sprout className="size-4" style={{ color: "#f5c2e7" }} />
          {seedCount} seed{seedCount === 1 ? "" : "s"}
        </span>
      </div>

      <RefineConceptModal
        open={refineOpen}
        onOpenChange={setRefineOpen}
        concept={concept}
        onAccepted={handleRefineAccepted}
      />
    </div>
  );
}
